import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Divider,
  Alert,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress
} from '@mui/material';
import ApiEndpoints from '../network/ApiEndPoints';
import { postJsonData } from '../network/ApiController';
import { apiErrorToast } from '../utils/ToastUtil';

function PaymentResponse() {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const clientTxnId = queryParams.get('client_txn_id');
  const [txnData, setTxnData] = useState(null);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (clientTxnId) {
      checkOrderStatus();
    }
  }, [clientTxnId]);

  const checkOrderStatus = () => {
    setIsLoading(true);
    setError('');

    postJsonData(
      ApiEndpoints.CHECK_ORDER_STATUS,
      {
        client_txn_id: clientTxnId,
        txn_date: new Date().toLocaleDateString('en-GB')
      },
      setIsLoading,
      (res) => {
        if (res.status) {
          setTxnData(res.data);
        } else {
          setError(res.msg || 'Failed to fetch transaction details');
        }
      },
      (err) => {
        apiErrorToast(err);
        setError('Failed to check payment status. Please try again later.');
      }
    );
  };

  const formatValue = (value) => {
    if (value === null || value === undefined) return '-';
    if (typeof value === 'object') return JSON.stringify(value);
    return value.toString();
  };

  return (
    <Paper elevation={3} sx={{ 
      p: 4,
      maxWidth: 800,
      mx: 'auto',
      my: 4
    }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Payment Status
      </Typography>
      <Typography variant="subtitle1" color="text.secondary" gutterBottom>
        Transaction details and status
      </Typography>
      <Divider sx={{ my: 3 }} />

      {isLoading && !txnData && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {txnData?.status && (
        <Alert
          severity={txnData.status === 'success' ? 'success' : 'error'}
          sx={{ mb: 3 }}
        >
          <Typography variant="subtitle1">
            Transaction Status: {txnData.status.toUpperCase()}
          </Typography>
          {txnData.status === 'success' && (
            <Typography variant="body2">
              Payment successful! Transaction ID: {txnData.upi_txn_id || 'N/A'}
            </Typography>
          )}
        </Alert>
      )}

      {txnData && (
        <TableContainer component={Paper} sx={{ mt: 3 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: 'primary.light' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>Field</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Value</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Object.entries(txnData).map(([key, value]) => (
                <TableRow key={key} hover>
                  <TableCell sx={{ fontWeight: 'medium' }}>
                    {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </TableCell>
                  <TableCell sx={{ wordBreak: 'break-word' }}>
                    {formatValue(value)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
}

export default PaymentResponse;